/**
 * Typed client for the SAP intelligence module — `/api/v1/sap-intel/*`.
 *
 * Mirrors the Pydantic schemas in
 * `backend/sap_intelligence/api/schemas.py`. Every call goes through the
 * shared `api` axios instance so it picks up the bearer token + the
 * apiWhitelist.
 */
import { api } from './api';

export type ColumnRole = 'dimension' | 'measure' | 'parameter' | string;

export interface ModelOverview {
  model_name: string;
  column_count: number;
  dimension_count: number;
  measure_count: number;
  parameter_count: number;
  user_modified_count: number;
  last_built_at: string | null;
  space_id: string | null;
}

export interface SapColumn {
  id: string;
  user_id: string | null;
  model_name: string;
  column_name: string;
  role: ColumnRole | null;
  data_type: string | null;
  sap_label: string | null;
  business_name: string | null;
  business_definition: string | null;
  description: string | null;
  usage_suggestions: string | null;
  sample_values: unknown[] | null;
  // Catch-all JSONB. The BE writes ``unit`` / ``unit_symbol`` /
  // ``synonyms`` at the ROOT (alongside SAP-side ``annotation`` /
  // ``column`` / ``parameter`` keys). The UI surfaces those three
  // for display and edits them via ``ColumnUpdatePayload``.
  column_metadata: Record<string, unknown> | null;
  embedding_model: string | null;
  embedding_dim: number | null;
  is_user_modified: boolean;
  space_id: string | null;
  metadata_url: string | null;
  data_url: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface BuildResponse {
  model_name: string;
  columns_written: number;
  columns_skipped: number;
  space_id: string | null;
  metadata_url: string | null;
  data_url: string | null;
  enrichment_warning?: string | null;
  enriched_count?: number;
  embedded_count?: number;
}

export interface FetchPlan {
  columns: string[];
  filters: Array<Record<string, unknown>>;
  input_parameters: Record<string, unknown>;
  period: string | null;
  fiscal_start_value: number | null;
  fiscal_end_value: number | null;
  fiscal_column: string | null;
  top: number | null;
  orderby: string | null;
}

export interface QueryResponse {
  model_name: string;
  query: string;
  fetch_plan: FetchPlan;
  api_url: string | null;
  rows: Array<Record<string, unknown>>;
  row_count: number;
  columns_used: string[];
  narrative: string | null;
  error: string | null;
}

export interface ColumnUpdatePayload {
  business_name?: string | null;
  business_definition?: string | null;
  description?: string | null;
  usage_suggestions?: string | null;
  // These three merge into ``column_metadata`` JSONB at the root on
  // the BE. Other keys in the blob (``annotation`` / ``column`` /
  // ``parameter``) survive the update.
  unit?: string | null;
  unit_symbol?: string | null;
  synonyms?: string[] | null;
}

const BASE = '/api/v1/sap-intel';

export const sapIntelligenceApi = {
  async listModels(): Promise<ModelOverview[]> {
    const { data } = await api.get<{ models: ModelOverview[] }>(`${BASE}/models`);
    return data?.models ?? [];
  },

  async listColumns(modelName: string): Promise<SapColumn[]> {
    const { data } = await api.get<{ model_name: string; columns: SapColumn[] }>(
      `${BASE}/models/${encodeURIComponent(modelName)}/columns`,
    );
    return data?.columns ?? [];
  },

  async buildModel(
    modelName: string,
    body: { scope?: 'global' | 'user'; model_description?: string } = {},
  ): Promise<BuildResponse> {
    const { data } = await api.post<BuildResponse>(
      `${BASE}/models/${encodeURIComponent(modelName)}/build`,
      body,
    );
    return data;
  },

  async refreshModel(
    modelName: string,
    body: { scope?: 'global' | 'user' } = {},
  ): Promise<BuildResponse> {
    const { data } = await api.post<BuildResponse>(
      `${BASE}/models/${encodeURIComponent(modelName)}/refresh`,
      body,
    );
    return data;
  },

  async deleteModel(
    modelName: string,
    scope: 'all' | 'global' | 'user' = 'all',
  ): Promise<{
    model_name: string;
    scope: 'all' | 'global' | 'user';
    deleted_global: number;
    deleted_user: number;
  }> {
    const { data } = await api.delete(
      `${BASE}/models/${encodeURIComponent(modelName)}?scope=${scope}`,
    );
    return data;
  },

  async updateColumn(columnId: string, payload: ColumnUpdatePayload): Promise<SapColumn> {
    const { data } = await api.patch<SapColumn>(`${BASE}/columns/${columnId}`, payload);
    return data;
  },

  async query(modelName: string, query: string, topK?: number): Promise<QueryResponse> {
    const { data } = await api.post<QueryResponse>(`${BASE}/query`, {
      model_name: modelName,
      query,
      top_k_columns: topK,
    });
    return data;
  },
};
